/**
 * useMediaPickerState - Custom hook for Media Picker state management
 * Extracted from Admin.jsx to reduce state complexity
 * 
 * Handles media selection for:
 * - Block content images
 * - Settings logo
 * - Welcome email header image
 * - Episode edit modal images
 * - Guest edit modal images
 * - Custom callbacks (giveaways, etc.)
 */
import { useState, useCallback } from 'react';

export const useMediaPickerState = ({
  // Setters from other hooks that need media picker integration
  setEditingGuest,
  setEditingEpisode,
  setSiteSettings,
  siteSettings,
  setWelcomeEmailSettings,
  selectedPage,
  setSelectedPage,
  updateBlockContent,
}) => {
  // Media picker state
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaPickerConfig, setMediaPickerConfig] = useState(null);

  // Open media picker for block content
  const handleOpenMediaPicker = useCallback((blockId, sectionIndex, blockIndex, contentKey) => {
    setMediaPickerConfig({ blockId, sectionIndex, blockIndex, contentKey });
    setShowMediaPicker(true);
  }, []);

  // Open media picker for settings logo
  const handleOpenLogoMediaPicker = useCallback(() => {
    setMediaPickerConfig({ isSettingsLogo: true });
    setShowMediaPicker(true);
  }, []);

  // Open media picker for welcome email header image
  const handleOpenWelcomeEmailMediaPicker = useCallback(() => {
    setMediaPickerConfig({ isWelcomeEmailHeader: true });
    setShowMediaPicker(true);
  }, []);

  // Open media picker for episode edit modal
  const handleOpenEpisodeMediaPicker = useCallback((field) => {
    setMediaPickerConfig({ episodeEdit: true, field });
    setShowMediaPicker(true);
  }, []);

  // Open media picker for guest edit modal
  const handleOpenGuestMediaPicker = useCallback((field) => {
    setMediaPickerConfig({ guestEdit: true, field });
    setShowMediaPicker(true);
  }, []);

  // Open media picker with custom callback
  const handleOpenCustomMediaPicker = useCallback((onSelect) => {
    setMediaPickerConfig({ onSelect });
    setShowMediaPicker(true);
  }, []);

  // Close media picker
  const closeMediaPicker = useCallback(() => {
    setShowMediaPicker(false);
    setMediaPickerConfig(null);
  }, []);

  // Handle media selection
  const handleMediaSelect = useCallback((mediaItem) => {
    if (!mediaPickerConfig) return;
    
    const { 
      blockId, 
      sectionIndex, 
      blockIndex, 
      contentKey, 
      isSettingsLogo, 
      isWelcomeEmailHeader, 
      episodeEdit, 
      guestEdit, 
      field, 
      onSelect 
    } = mediaPickerConfig;
    
    // Handle custom onSelect callback (used by Giveaway editor, etc.)
    if (onSelect && typeof onSelect === 'function') {
      onSelect(mediaItem);
      closeMediaPicker();
      return;
    }
    
    // Handle guest edit modal media selection
    if (guestEdit && field && setEditingGuest) {
      setEditingGuest(prev => ({
        ...prev,
        [field]: mediaItem.url
      }));
      closeMediaPicker();
      return;
    }
    
    // Handle episode edit modal media selection
    if (episodeEdit && field && setEditingEpisode) {
      setEditingEpisode(prev => ({
        ...prev,
        [field]: mediaItem.url
      }));
      closeMediaPicker();
      return;
    }
    
    // Handle settings logo selection
    if (isSettingsLogo && setSiteSettings && siteSettings) {
      setSiteSettings({ ...siteSettings, logoUrl: mediaItem.url });
      closeMediaPicker();
      return;
    }
    
    // Handle welcome email header image selection
    if (isWelcomeEmailHeader && setWelcomeEmailSettings) {
      setWelcomeEmailSettings(prev => ({ ...prev, headerImage: mediaItem.url }));
      closeMediaPicker();
      return;
    }
    
    // Handle regular block content
    if (selectedPage && setSelectedPage && updateBlockContent) {
      const updatedSections = [...selectedPage.sectionsData];
      updatedSections[sectionIndex].blocksData[blockIndex].content[contentKey] = mediaItem.url;
      setSelectedPage({ ...selectedPage, sectionsData: updatedSections });
      
      // Update the backend
      updateBlockContent(blockId, contentKey, mediaItem.url);
    }
    
    // Close the picker
    closeMediaPicker();
  }, [
    mediaPickerConfig, 
    closeMediaPicker, 
    setEditingGuest, 
    setEditingEpisode, 
    setSiteSettings, 
    siteSettings, 
    setWelcomeEmailSettings, 
    selectedPage, 
    setSelectedPage, 
    updateBlockContent
  ]);

  return {
    // State
    showMediaPicker,
    mediaPickerConfig,

    // Setters (for components that need direct access)
    setShowMediaPicker,
    setMediaPickerConfig,

    // Actions
    handleOpenMediaPicker,
    handleOpenLogoMediaPicker,
    handleOpenWelcomeEmailMediaPicker,
    handleOpenEpisodeMediaPicker,
    handleOpenGuestMediaPicker,
    handleOpenCustomMediaPicker,
    closeMediaPicker,
    handleMediaSelect,
  };
};

export default useMediaPickerState;
